import { BlockType } from '../types'

export const BRAND_PALETTE = [
  '#76448A', // Ciruela
  '#45B39D', // Verde mar / Teal
  '#D98880', // Rosa viejo
  '#73C6B6', // Menta desaturado
  '#D7BDE2', // Lavanda suave
  '#E59866', // Terracota / Salmón opaco
  '#5D6D7E', // Pizarra / Gris acero
  '#F5B041', // Mostaza cálido
  '#85929E', // Gris plomo
  '#148F77', // Verde pino / Esmeralda oscuro
  '#C39BD3', // Lila
  '#9B59B6', // Púrpura suave
  '#546E7A', // Gris azulado desaturado
  '#AF7AC5', // Violeta
  '#F0B27A', // Durazno
  '#A569BD', // Orquídea
  '#117A65', // Teal oscuro
  '#B2BABB', // Gris plata
  '#884EA0', // Uva
  '#D2B4DE', // Cardo (morado pálido)
  '#616A6B', // Grafito
  '#A2D9CE', // Turquesa pálido
  '#E6B0AA', // Coral suave
  '#BB8FCE', // Lavanda medio
  '#A3E4D7', // Menta claro
  '#85929E', // Gris medio
  '#D5F5E3', // Verde muy pálido
  '#E8DAEF', // Violeta muy pálido
  '#FADBD8', // Rosa pálido
  '#34495E', // Carbón
]

export const MULTI_BRAND_COLOR = '#D4A017'

/** Marca o zona sin color asignado en la paleta. */
export const UNKNOWN_CATEGORY_COLOR = '#90A4AE'

/**
 * Escala de grises azulados del chrome de la página: textos, bordes y
 * superficies de tarjetas, tooltips y modales.
 *
 * Nombrarla una sola vez evita que el mismo borde aparezca como '#E2E8F0' en un
 * archivo y '#e2e8f0' en otro, que es lo que pasaba antes de centralizarla.
 */
export const SLATE_950 = '#0F172A'
export const SLATE_900 = '#1E293B'
export const SLATE_700 = '#334155'
export const SLATE_500 = '#64748B'
export const SLATE_400 = '#94A3B8'
export const SLATE_300 = '#CBD5E1'
export const SLATE_200 = '#E2E8F0'
export const SLATE_100 = '#F1F5F9'
export const SLATE_50 = '#F8FAFC'

/** Superficie gris muy clara: fondo de la grilla, de la leyenda y de las tablas. */
export const SURFACE_MUTED = '#fafafa'
/** Borde neutro de contenedores y separadores dentro de los modales. */
export const BORDER_SUBTLE = '#e0e0e0'
export const WHITE = '#FFFFFF'

/** Badge de zona sin color asignado, y badge de posición bloqueada. */
export const ZONE_BADGE_FALLBACK_BG = '#D1FAE5'
export const ZONE_BADGE_FALLBACK_TEXT = '#065F46'
export const BLOCKED_BADGE_BG = '#FEE2E2'
export const BLOCKED_BADGE_TEXT = '#DC2626'

/**
 * Escala de ocupación: el tramo de cada nivel y el color con que se pinta.
 *
 * Única fuente de la escala. La celda elige color recorriendo `maxPct` y la
 * leyenda arma sus etiquetas del mismo arreglo, así que mover un corte actualiza
 * las dos cosas a la vez. Antes el umbral vivía en los dos componentes de celda
 * y el texto del tramo en la leyenda: bastaba tocar uno para que la leyenda
 * anunciara un corte que el mapa ya no usaba.
 */
export const OCCUPANCY_SCALE = [
  { maxPct: 25, color: '#22C55E' },
  { maxPct: 80, color: '#FFA726' },
  { maxPct: 100, color: '#EF5350' },
] as const

/** Fondo de una posición sin stock: gris en Ocupación, blanco en los otros modos. */
export const EMPTY_CELL_BG_OCCUPANCY = '#f5f5f5'
export const EMPTY_CELL_BG = '#ffffff'

/** Transparencia con la que se pinta el fondo de cada celda. */
export const CELL_ALPHA = 0.6
/**
 * Superficie sobre la que se dibujan las celdas; base para calcular contraste.
 * Atada a `SURFACE_MUTED` a propósito: si cambia el fondo de la grilla, el
 * cálculo de contraste tiene que medirse contra el fondo nuevo.
 */
export const CELL_SURFACE = SURFACE_MUTED
export const CELL_RADIUS = '4px'
export const CELL_BORDER = '1px solid rgba(0,0,0,0.08)'
/** Azul de acento de la página: realce de celda y hover del botón Recalcular. */
export const ACCENT_BLUE = '#004bb1'
/** Realce de la celda con el resumen abierto. */
export const CELL_SELECTED_OUTLINE = `2px solid ${ACCENT_BLUE}`
/** Opacidad de las celdas atenuadas por el filtro de marca. */
export const CELL_DIMMED_OPACITY = 0.18
/** Texto de las celdas vacías, sobre fondo blanco. */
export const EMPTY_CELL_TEXT = '#708397'
export const CELL_FONT_SIZE = '0.6875rem'
export const CELL_FONT_WEIGHT = 600

/** Color del triángulo que marca una posición con slots. */
export const SLOT_MARKER_COLOR = '#1A1A1A'

export const LEVEL_CHIP_W = 82
/** Ancho de la columna de chips de pasillo en la grilla principal. */
export const AISLE_CHIP_W = 80
/** Alto de las cabeceras de columna, igual en la grilla y en la vista frontal. */
export const AXIS_HEADER_H = 32
/** Borde izquierdo que marca el pasillo abierto. */
export const AISLE_ACTIVE_ACCENT = '#90CAF9'
export const HATCH_BACKGROUND =
  'repeating-linear-gradient(45deg, #e0e0e0 0px, #e0e0e0 2px, #f5f5f5 2px, #f5f5f5 8px)'
// Encabezado del eje de filas (pasillo en la grilla, nivel en la vista frontal).
export const GRID_ROW_BASE_BG = '#0b68b5'
export const GRID_ROW_HOVER_BG = '#0a5c9f'
export const GRID_ROW_ACTIVE_BG = '#084e88'

// Encabezado del eje de columnas.
export const GRID_COL_HEADER_BG = '#e1ebf5'
export const GRID_COL_HEADER_TEXT = '#26465f'
export const GRID_COL_HEADER_BORDER = '#afc4d9'

// Barra de título de los modales; alineada al azul del eje de filas para no
// tener dos azules casi iguales conviviendo en la misma pantalla.
export const AISLE_HEADER_BG = GRID_ROW_BASE_BG

/**
 * Modos de coloreo del mapa.
 *
 * Objeto `as const` y no `enum` de TypeScript: el tipo derivado sigue siendo la
 * unión de literales que ya usa toda la página, así que `mode === 'zone'` y
 * `useState<ViewMode>('brand')` siguen compilando igual. Mismo idioma que
 * `ExpiredColorStatus` en `utils/colors`.
 */
export const MapViewMode = {
  brand: 'brand',
  occupancy: 'occupancy',
  zone: 'zone',
} as const

export type ViewMode = (typeof MapViewMode)[keyof typeof MapViewMode]

export const MAP_MODE_LABELS: Record<ViewMode, string> = {
  [MapViewMode.brand]: 'Marca',
  [MapViewMode.occupancy]: 'Ocupación',
  [MapViewMode.zone]: 'Zona',
}

export interface ProductsCellRef {
  aisle: string
  column: string
  level?: string
  totalStock: number
  maxCapacity: number
  occupancyPct: number
}

export const blockLabel: Record<BlockType, string> = {
  none: 'Sin bloqueo',
  ingress: 'Bloq. ingreso',
  egress: 'Bloq. egreso',
  both: 'Bloq. total',
}
